import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { v2Api } from '@/lib/api';
import type { ExerciseFlagMapping, ExerciseTeamMapping, FlagModelPayload } from '@/lib/api/v2';
import { validateFlagScoreAndHintPenalty } from '@/lib/flagScoreHintValidation';
import { Pencil, Plus, Trash2, AlertCircle, ChevronDown } from 'lucide-react';

type PhaseOption = { value: number; label: string };

interface ExerciseFlagsCardProps {
  exerciseId: string;
  title?: string;
  initialFlags?: ExerciseFlagMapping[];
  initialMappings?: ExerciseTeamMapping[];
  exercisePhases?: PhaseOption[];
  onRefetch?: () => Promise<void>;
}

type ScoreTypeValue = 'standard' | 'manual' | 'decay';

const SCORE_TYPE_OPTIONS: { value: ScoreTypeValue; label: string; hint: string }[] = [
  { value: 'standard', label: 'Standard', hint: 'Full points when solved.' },
  { value: 'manual', label: 'Manual', hint: 'Score set by facilitator.' },
  { value: 'decay', label: 'Decay', hint: 'Points decrease over time.' },
];

function normalizeScoreType(raw: string | undefined): ScoreTypeValue {
  const s = (raw ?? 'standard').toLowerCase();
  if (s === 'manual' || s === 'decay') return s;
  return 'standard';
}

function scoreTypeLabel(t: string | undefined): string {
  return SCORE_TYPE_OPTIONS.find((o) => o.value === normalizeScoreType(t))?.label ?? 'Standard';
}

type FormState = {
  exercise_team: number;
  question: string;
  answer: string;
  hint: string;
  placeholder: string;
  hint_penalty: string;
  score: string;
  score_type: ScoreTypeValue;
  phase: string; // '' means none
  is_required: boolean;
  order: number;
};

const emptyForm: FormState = {
  exercise_team: 0,
  question: '',
  answer: '',
  hint: '',
  placeholder: '',
  hint_penalty: '',
  score: '',
  score_type: 'standard',
  phase: '',
  is_required: true,
  order: 1,
};

function parseOptionalInt(s: string): number | null {
  const trimmed = s.trim();
  if (!trimmed) return null;
  const n = parseInt(trimmed, 10);
  return Number.isNaN(n) ? null : n;
}

export function ExerciseFlagsCard({
  exerciseId,
  title = 'Team Flags',
  initialFlags,
  initialMappings,
  exercisePhases,
  onRefetch,
}: ExerciseFlagsCardProps) {
  const [flags, setFlags] = useState<ExerciseFlagMapping[]>(initialFlags ?? []);
  const [mappings, setMappings] = useState<ExerciseTeamMapping[]>(initialMappings ?? []);
  const [loading, setLoading] = useState(initialFlags === undefined || initialMappings === undefined);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ExerciseFlagMapping | null>(null);

  const [form, setForm] = useState<FormState>(emptyForm);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<ExerciseFlagMapping | null>(null);
  // `undefined` means "not selected yet" (initial load); `null` is a real collapsed state.
  const [openPhaseKey, setOpenPhaseKey] = useState<string | undefined>(undefined);
  const [teamFilter, setTeamFilter] = useState<number | 'all'>('all');

  const mappingById = useMemo(() => new Map(mappings.map((m) => [m.id, m])), [mappings]);
  const teamOptions = useMemo(() => {
    const byTeam = new Map<number, { id: number; team_name: string; team_code: string }>();
    for (const m of mappings ?? []) {
      if (!byTeam.has(m.team)) {
        byTeam.set(m.team, { id: m.team, team_name: m.team_name, team_code: m.team_code });
      }
    }
    return Array.from(byTeam.values());
  }, [mappings]);

  const filteredFlagsCount = useMemo(() => {
    if (teamFilter === 'all') return flags.length;
    return (flags ?? []).filter((f) => f.flag_team === teamFilter).length;
  }, [flags, teamFilter]);

  useEffect(() => {
    if (initialFlags !== undefined) setFlags(initialFlags);
  }, [initialFlags]);

  useEffect(() => {
    if (initialMappings !== undefined) setMappings(initialMappings);
  }, [initialMappings]);

  useEffect(() => {
    // If parent already gave us data, avoid extra calls.
    if (initialFlags !== undefined && initialMappings !== undefined) {
      setLoading(false);
    }
  }, [initialFlags, initialMappings]);

  useEffect(() => {
    // Close accordions when team filter changes.
    setOpenPhaseKey(undefined);
  }, [teamFilter]);

  const refresh = async () => {
    if (onRefetch) {
      await onRefetch();
      return;
    }
  };

  const openCreate = () => {
    setEditing(null);
    setError(null);
    setForm({
      ...emptyForm,
      exercise_team: mappings[0]?.id ?? 0,
      phase: '',
      placeholder: '',
      score_type: 'standard',
      order: (flags.length ?? 0) + 1,
      is_required: true,
    });
    setDialogOpen(true);
  };

  const openEdit = (item: ExerciseFlagMapping) => {
    setEditing(item);
    setError(null);
    setForm({
      ...emptyForm,
      exercise_team: item.exercise_team ?? 0,
      question: item.flag_question ?? '',
      answer: item.flag_answer ?? '',
      hint: item.flag_hint ?? '',
      placeholder: item.flag_placeholder ?? '',
      hint_penalty: item.flag_hint_penalty != null ? String(item.flag_hint_penalty) : '',
      score: item.flag_score != null ? String(item.flag_score) : '',
      score_type: normalizeScoreType(item.flag_score_type),
      phase: item.flag_phase != null ? String(item.flag_phase) : '',
      is_required: item.is_required ?? true,
      order: item.order ?? 1,
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditing(null);
    setForm(emptyForm);
    setError(null);
  };

  const handleSave = async () => {
    if (!form.exercise_team) {
      setError('Please select an exercise-team mapping.');
      return;
    }
    if (!form.question.trim()) {
      setError('Question is required.');
      return;
    }
    if (!form.answer.trim()) {
      setError('Answer is required.');
      return;
    }
    const hintPenalty = parseOptionalInt(form.hint_penalty);
    const score = parseOptionalInt(form.score);

    if (score == null) {
      setError('Score is required.');
      return;
    }

    const scoreHintErr = validateFlagScoreAndHintPenalty(score, hintPenalty);
    if (scoreHintErr) {
      setError(scoreHintErr.hint_penalty ?? scoreHintErr.score ?? 'Invalid score or hint penalty.');
      return;
    }

    const selectedMapping = mappingById.get(form.exercise_team);
    if (!selectedMapping) {
      setError('Invalid exercise-team mapping selection.');
      return;
    }

    const flagPayload: FlagModelPayload = {
      team: selectedMapping.team,
      phase: form.phase ? parseInt(form.phase, 10) : null,
      question: form.question.trim(),
      answer: form.answer.trim(),
      hint: form.hint.trim(),
      placeholder: form.placeholder.trim(),
      hint_penalty: hintPenalty,
      score,
      score_type: form.score_type,
    };

    setSaving(true);
    setError(null);
    try {
      if (editing) {
        // 1) Update underlying flag record
        await v2Api.dashboard.flags.update(editing.flag, {
          ...flagPayload,
        });
        // 2) Update only mapping fields
        await v2Api.dashboard.exerciseFlags.update(exerciseId, editing.id, {
          exercise_team: form.exercise_team,
          is_required: form.is_required,
          order: form.order,
        });
      } else {
        // 1) Create a new flag record
        const createdFlag = await v2Api.dashboard.flags.create(flagPayload);
        // 2) Create the mapping for this exercise-team
        await v2Api.dashboard.exerciseFlags.create(exerciseId, {
          exercise_team: form.exercise_team,
          flag: createdFlag.id,
          is_required: form.is_required,
          order: form.order,
        });
      }
      closeDialog();
      await refresh();
    } catch (err: unknown) {
      const data = (err as { response?: { data?: { detail?: string; errors?: Record<string, unknown> } } })?.response?.data;
      const msg = data?.detail ?? (err instanceof Error ? err.message : 'Failed to save flag mapping');

      // Prefer specific field error messages when present.
      if (data?.errors && typeof data.errors === 'object') {
        const firstKey = Object.keys(data.errors)[0];
        const v = (data.errors as Record<string, unknown>)[firstKey];
        if (typeof v === 'string') {
          setError(v);
          setSaving(false);
          return;
        }
      }
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const openDelete = (item: ExerciseFlagMapping) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    setSaving(true);
    setError(null);
    try {
      await v2Api.dashboard.exerciseFlags.delete(exerciseId, itemToDelete.id);
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      await refresh();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
        (err instanceof Error ? err.message : 'Failed to delete flag mapping');
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const phaseGroups = useMemo(() => {
    const groups: Array<{ key: string; label: string; items: ExerciseFlagMapping[] }> = [];
    const byKey = new Map<string, ExerciseFlagMapping[]>();

    for (const p of exercisePhases ?? []) {
      byKey.set(String(p.value), []);
    }
    const untyped: ExerciseFlagMapping[] = [];

    for (const f of flags ?? []) {
      if (teamFilter !== 'all' && f.flag_team !== teamFilter) continue;
      if (f.flag_phase == null) {
        untyped.push(f);
        continue;
      }
      const k = String(f.flag_phase);
      if (!byKey.has(k)) byKey.set(k, []);
      byKey.get(k)!.push(f);
    }

    for (const p of exercisePhases ?? []) {
      const k = String(p.value);
      const items = byKey.get(k) ?? [];
      if (items.length > 0) groups.push({ key: k, label: p.label, items });
    }

    if (untyped.length > 0) {
      groups.push({ key: 'none', label: 'No type (unassigned)', items: untyped });
    }

    return groups;
  }, [flags, exercisePhases, teamFilter]);

  useEffect(() => {
    if (!phaseGroups.length) {
      setOpenPhaseKey(undefined);
      return;
    }
    // Start with all accordions closed; only validate existing key.
    if (openPhaseKey === undefined) return;
    if (!phaseGroups.some((g) => g.key === openPhaseKey)) setOpenPhaseKey(undefined);
  }, [phaseGroups, openPhaseKey]);

  return (
    <Card>
      <CardHeader className="space-y-1">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-lg">{title}</CardTitle>
            <CardDescription>{filteredFlagsCount} flag(s)</CardDescription>
          </div>

          <div className="flex items-center gap-3">
            {mappings.length > 0 && teamOptions.length > 0 && (
              <div className="flex items-center gap-2">
                <Label
                  htmlFor="team-flag-filter"
                  className="text-xs text-muted-foreground whitespace-nowrap"
                >
                  Filter by team
                </Label>
                <Select
                  id="team-flag-filter"
                  value={teamFilter === 'all' ? '' : String(teamFilter)}
                  onChange={(e) => {
                    const v = e.target.value;
                    setTeamFilter(v === '' ? 'all' : parseInt(v, 10));
                  }}
                >
                  <option value="">All teams</option>
                  {teamOptions.map((t) => (
                    <option key={t.id} value={String(t.id)}>
                      {t.team_name}
                    </option>
                  ))}
                </Select>
              </div>
            )}

            <Button type="button" size="sm" onClick={openCreate} disabled={mappings.length === 0}>
              <Plus className="h-4 w-4 mr-2" />
              Add
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {error && <p className="text-sm text-destructive mb-3">{error}</p>}

        {loading ? (
          <p className="text-sm text-muted-foreground py-2">Loading flags...</p>
        ) : mappings.length === 0 ? (
          <div className="flex items-start gap-2 text-sm text-muted-foreground py-2">
            <AlertCircle className="h-4 w-4 mt-0.5" />
            <p>
              No exercise team mappings found. Create mappings first in the “Exercise Team Mappings” section.
            </p>
          </div>
        ) : flags.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2">No flags yet.</p>
        ) : phaseGroups.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2">No flags match the selected team.</p>
        ) : (
          <div className="space-y-2">
            {phaseGroups.map((group) => {
              const isOpen = openPhaseKey === group.key;
              return (
                <div
                  key={group.key}
                  className="rounded-lg border bg-muted/20 overflow-hidden"
                >
                  <button
                    type="button"
                    className="w-full flex items-center justify-between gap-3 p-3 hover:bg-muted/40"
                    onClick={() => setOpenPhaseKey(isOpen ? undefined : group.key)}
                    aria-expanded={isOpen}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-medium text-sm truncate">{group.label}</span>
                      <Badge variant="outline" className="text-xs shrink-0">
                        {group.items.length} flag{group.items.length === 1 ? '' : 's'}
                      </Badge>
                    </div>
                    <ChevronDown
                      className={`h-4 w-4 text-muted-foreground transition-transform ${
                        isOpen ? 'rotate-180' : 'rotate-0'
                      }`}
                    />
                  </button>

                  {isOpen && (
                    <div className="p-3 pt-0 space-y-2">
                      {group.items.map((item) => {
                        const mapping = item.exercise_team
                          ? mappingById.get(item.exercise_team)
                          : undefined;
                        const teamLabel =
                          item.flag_team_name ||
                          mapping?.team_name ||
                          `Team #${item.flag_team}`;
                        return (
                          <div
                            key={item.id}
                            className="flex items-start justify-between gap-3 rounded border bg-card px-3 py-2"
                          >
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium truncate">
                                {item.flag_question || 'Untitled flag'}
                              </p>
                              <div className="flex items-center gap-2 mt-1 flex-wrap">
                                <Badge variant="secondary" className="text-xs">
                                  {teamLabel}
                                </Badge>
                                <Badge variant="outline" className="text-xs">
                                  {item.flag_score} pts
                                </Badge>
                                <Badge variant="outline" className="text-xs">
                                  {scoreTypeLabel(item.flag_score_type)}
                                </Badge>
                                {item.flag_hint_penalty != null ? (
                                  <Badge variant="secondary" className="text-xs">
                                    Penalty {item.flag_hint_penalty}
                                  </Badge>
                                ) : null}
                                {!item.is_required ? (
                                  <Badge variant="secondary" className="text-xs">
                                    Optional
                                  </Badge>
                                ) : null}
                              </div>
                            </div>

                            <div className="flex items-center gap-1 shrink-0">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => openEdit(item)}
                                disabled={saving}
                                aria-label="Edit flag"
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive hover:text-destructive"
                                onClick={() => openDelete(item)}
                                disabled={saving}
                                aria-label="Delete flag"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent className="flex max-h-[min(90vh,calc(100dvh-1.5rem))] min-h-0 w-full max-w-[520px] flex-col gap-0 overflow-hidden p-0 sm:max-h-[min(90vh,calc(100dvh-2rem))]">
          <div className="shrink-0 border-b border-border px-6 pb-3 pt-6 pr-12">
            <DialogHeader>
              <DialogTitle>{editing ? 'Edit flag' : 'Add flag'}</DialogTitle>
              <DialogDescription>Create a flag tied to an exercise team mapping.</DialogDescription>
            </DialogHeader>
          </div>

          <div className="flex min-h-0 flex-1 flex-col">
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-4">
              <div className="grid gap-4">
            {error && <p className="text-sm text-destructive">{error}</p>}

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label htmlFor="flag-exercise-team">Exercise Team Mapping *</Label>
                <Select
                  id="flag-exercise-team"
                  value={form.exercise_team ? String(form.exercise_team) : ''}
                  onChange={(e) => setForm((f) => ({ ...f, exercise_team: parseInt(e.target.value, 10) || 0 }))}
                >
                  <option value="">Select mapping</option>
                  {mappings.map((m) => (
                    <option key={m.id} value={String(m.id)}>
                      {m.team_name}
                    </option>
                  ))}
                </Select>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="flag-phase">Phase (optional)</Label>
                <Select
                  id="flag-phase"
                  value={form.phase ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, phase: e.target.value }))}
                >
                  <option value="">Select phase (optional)</option>
                  {(exercisePhases ?? []).map((p) => (
                    <option key={p.value} value={String(p.value)}>
                      {p.label}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="flag-question">Question *</Label>
              <Input
                id="flag-question"
                value={form.question}
                onChange={(e) => setForm((f) => ({ ...f, question: e.target.value }))}
                placeholder="Enter the flag question"
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="flag-placeholder">Placeholder</Label>
              <Input
                id="flag-placeholder"
                value={form.placeholder}
                onChange={(e) => setForm((f) => ({ ...f, placeholder: e.target.value }))}
                placeholder="(optional) e.g. Enter ticket number"
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="flag-answer">Answer *</Label>
              <Input
                id="flag-answer"
                value={form.answer}
                onChange={(e) => setForm((f) => ({ ...f, answer: e.target.value }))}
                placeholder="Enter the correct answer"
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="flag-hint">Hint (optional)</Label>
              <Input
                id="flag-hint"
                value={form.hint}
                onChange={(e) => setForm((f) => ({ ...f, hint: e.target.value }))}
                placeholder="Enter the hint"
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="flag-score">Score *</Label>
                <Input
                  id="flag-score"
                  type="number"
                  min={0}
                  value={form.score}
                  onChange={(e) => setForm((f) => ({ ...f, score: e.target.value }))}
                  placeholder="e.g. 10"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="flag-hint-penalty">Hint penalty (optional)</Label>
                <Input
                  id="flag-hint-penalty"
                  type="number"
                  min={0}
                  value={form.hint_penalty}
                  onChange={(e) => setForm((f) => ({ ...f, hint_penalty: e.target.value }))}
                  placeholder="e.g. 0"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 items-start sm:grid-cols-2">
              <div className="grid gap-2">
                <Label htmlFor="flag-score-type">Score type</Label>
                <Select
                  id="flag-score-type"
                  value={form.score_type}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, score_type: normalizeScoreType(e.target.value) }))
                  }
                >
                  {SCORE_TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
                <p className="text-xs text-muted-foreground">
                  {SCORE_TYPE_OPTIONS.find((o) => o.value === form.score_type)?.hint}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="flag-order">Display order</Label>
                <Input
                  id="flag-order"
                  type="number"
                  min={1}
                  value={form.order}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      order: Math.max(1, parseInt(e.target.value, 10) || 1),
                    }))
                  }
                />
              </div>
            </div>
              </div>
            </div>
          </div>

          <div className="shrink-0 border-t border-border bg-background px-6 py-4">
            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={closeDialog} disabled={saving}>
                Cancel
              </Button>
              <Button type="button" onClick={handleSave} disabled={saving}>
                {saving ? (editing ? 'Saving...' : 'Creating...') : editing ? 'Save changes' : 'Add flag'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteDialogOpen} onOpenChange={(open) => !open && setDeleteDialogOpen(false)}>
        <DialogContent className="w-full max-w-[420px]">
          <DialogHeader className="pr-8">
            <DialogTitle>Delete flag</DialogTitle>
            <DialogDescription>This will remove the flag mapping from the exercise.</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button type="button" variant="destructive" onClick={handleDeleteConfirm} disabled={saving || !itemToDelete}>
              {saving ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

